import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, MapPin, Users, Mail, Phone, User, ArrowLeft, ChevronDown, Search, Briefcase } from "lucide-react";
import GoogleSearch from "./search/googleAutoComplete";
import CustomSelect from "./ui/CustomSelect";
import { sendData } from "./formSubmission";
import distanceCalculator from "./search/distanceCalculator";
import durationCalculator from "./search/durationCalculator";
import { priceCalculationNormal, priceCalculationLuxury, priceCalculationVan } from "./search/priceCalculation";
import taxiNormal from "../image/taxiNormal.jpg";
import taxiLuxury from "../image/taxiLuxury.jpg";
import taxiVan from "../image/taxiVan.jpg";
import parkGuellImg from "../image/location/parkguell.jpg";

/**
 * react component to return the html form 
 * @returns a html form with data related to the taxi journey
 */
function MainForm({ onBookingActiveChange }) {
    const { t } = useTranslation();
    const formRef = useRef(null);

    const [step, setStep] = useState('search');
    const [direction, setDirection] = useState('to_airport'); // 'from_airport' | 'to_airport'

    // --- Journey State ---
    const [fromLocation, setFromLocation] = useState("");
    const [toLocation, setToLocation] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [passengers, setPassengers] = useState(1);

    // --- Personal Details State ---
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [number, setNumber] = useState('');
    const [countryCode, setCountryCode] = useState('+34');

    // --- UI State ---
    const [isCalculating, setIsCalculating] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // --- Pricing & Vehicle States ---
    const [distanceKm, setDistanceKm] = useState(null);
    const [duration, setDuration] = useState("");
    const [priceNormal, setPriceNormal] = useState(null);
    const [priceLuxury, setPriceLuxury] = useState(null);
    const [priceVan, setPriceVan] = useState(null);
    const [carType, setCarType] = useState("normal");
    const [suitcases, setSuitcases] = useState(1);
    const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);

    const getTodayDateString = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const minDate = getTodayDateString();

    const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    const minutes = ['00', '15', '30', '45'];

    const terminalOptions = [
        { value: "Aeropuerto Josep Tarradellas Barcelona-El Prat (BCN), T1", label: t('form.t1') || "Barcelona Airport T1" },
        { value: "Aeropuerto Josep Tarradellas Barcelona-El Prat (BCN), T2", label: t('form.t2') || "Barcelona Airport T2" },
        { value: "unknown_terminal", label: t('form.terminal_unknown') || "I don't know my terminal" }
    ];

    const handleNumberChange = (e) => {
        const rawValue = e.target.value;
        const filteredValue = rawValue.replace(/[^0-9]/g, '');
        const limitedValue = filteredValue.slice(0, 10);
        setNumber(limitedValue);
    };


    // Automatically recalculate distance, duration, and prices when addresses change
    useEffect(() => {
        const calculateRouteDetails = async () => {
            if (!fromLocation || !toLocation) {
                setDistanceKm(null);
                setDuration("");
                setPriceNormal(null);
                setPriceLuxury(null);
                setPriceVan(null);
                return;
            }

            setIsCalculating(true);

            // Fallback for unknown terminal to standard El Prat airport address
            const startAddress = fromLocation === "unknown_terminal"
                ? "Aeropuerto Josep Tarradellas Barcelona-El Prat (BCN)"
                : fromLocation;

            const endAddress = toLocation === "unknown_terminal"
                ? "Aeropuerto Josep Tarradellas Barcelona-El Prat (BCN)"
                : toLocation;

            try {
                const [distance, dur] = await Promise.all([
                    distanceCalculator(startAddress, endAddress),
                    durationCalculator(startAddress, endAddress)
                ]);

                setDistanceKm(distance);
                setDuration(dur);

                const [eurosNormal, centsNormal] = await priceCalculationNormal(distance);
                const [eurosLuxury, centsLuxury] = await priceCalculationLuxury(distance);
                const [eurosVan, centsVan] = await priceCalculationVan(distance);

                setPriceNormal([eurosNormal, centsNormal]);
                setPriceLuxury([eurosLuxury, centsLuxury]);
                setPriceVan([eurosVan, centsVan]);

            } catch (error) {
                console.error("Error calculating routes/prices:", error);
                setDistanceKm(null);
                setDuration("");
                setPriceNormal(null);
                setPriceLuxury(null);
                setPriceVan(null);
            } finally {
                setIsCalculating(false);
            }
        };

        calculateRouteDetails();
    }, [fromLocation, toLocation]);

    // Automatically select Van if passengers or suitcases exceed 4
    useEffect(() => {
        if (passengers >= 5 || suitcases >= 5) {
            setCarType("van");
        } else {
            setCarType("normal");
        }
    }, [passengers, suitcases]);

    useEffect(() => {
        if (onBookingActiveChange) {
            onBookingActiveChange(step === 'details');
        }
    }, [step, onBookingActiveChange]);

    // "Book Ride" (Go to Details) ---
    const handleBookRide = (e) => {
        e.preventDefault();
        // Basic validation
        if (!fromLocation || !toLocation || !date || !time) {
            // In a real app, show a toast or error. For now, the button is disabled if fields are empty, 
            // but we double check here.
            return;
        }
        setStep('details');

        // Scroll the form into view smoothly, ensuring it doesn't get hidden behind the fixed header
        setTimeout(() => {
            if (formRef.current) {
                // Add scroll-margin-top dynamically to offset the fixed header
                formRef.current.style.scrollMarginTop = window.innerWidth < 768 ? "120px" : "100px";
                formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 150);
    };

    // Final Submission ---
    const handleFinalBooking = async (e) => {
        e.preventDefault();

        if (!name || !number || number.length < 9) {
            alert(t('form.modal.missing_info_message') || "Please fill in all required fields.");
            return;
        }

        setIsCalculating(true);

        const fullPhoneNumber = `${countryCode}${number}`;

        try {
            const selectedPriceArray = carType === "van"
                ? priceVan
                : priceNormal;

            const selectedPrice = selectedPriceArray || [0, 0];
            const selectedCarLabel = carType === "van" ? "Minivan" : "Standard Sedan";
            const detailedMessage = `[Vehículo: ${selectedCarLabel} | Maletas: ${suitcases}]${message ? ' ' + message : ''}`;

            const result = await sendData(
                fromLocation,
                toLocation,
                carType,
                distanceKm || 0,
                selectedPrice,
                duration || "0 mins",
                date,
                time,
                detailedMessage,
                name,
                fullPhoneNumber,
                email,
                null,
                passengers
            );

            if (result.status === "success") {

                setStep('searching');

                // Simulate "searching for drivers" for 4 seconds
                setTimeout(() => {
                    setStep('success');
                    setTimeout(() => {
                        if (formRef.current) {
                            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 100);
                }, 4000);
            } else {
                setErrorMessage(result.message || "Error processing booking");
                setShowErrorModal(true);
            }
        } catch (error) {
            // console.error("Booking error:", error);
            setErrorMessage("An error occurred. Please try again.");
            setShowErrorModal(true);
        } finally {
            setIsCalculating(false);
        }
    };


    return (
        <div ref={formRef} className="w-full max-w-[400px] md:max-w-6xl mx-auto font-montserrat relative z-20">
            <div className="bg-white/95 backdrop-blur-xl rounded-[32px] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.12)] border border-white/50 overflow-hidden">
                {/* Back Button for non-search steps */}
                {step !== 'search' && (
                    <div className="p-3 border-b border-gray-100 flex items-center gap-2 md:hidden">
                        <button
                            onClick={() => setStep('search')}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4 text-gray-600" />
                        </button>
                        <span className="font-semibold text-sm text-gray-800">
                            {t('form.modal.details_title') || "Complete Booking"}
                        </span>
                    </div>
                )}

                {/* Desktop Back Button */}
                {step !== 'search' && (
                    <div className="hidden md:flex items-center gap-2 p-4 border-b border-gray-100">
                        <button
                            onClick={() => setStep('search')}
                            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            {t('form.modal.back') || "Back"}
                        </button>
                    </div>
                )}

                <div className="p-4 md:p-2">
                    {/* SEARCH STEP */}
                    {step === 'search' && (
                        <div className="flex flex-col gap-4 animate-in slide-in-from-left-4 duration-300">
                            {/* Direction Toggle */}
                            <div className="flex justify-center mb-6">
                                <div className="inline-flex p-1.5 bg-gray-100/80 rounded-2xl relative backdrop-blur-sm border border-gray-200/50 shadow-inner">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDirection('to_airport');
                                            setFromLocation('');
                                            setToLocation('');
                                        }}
                                        className={`px-8 py-2.5 text-sm font-extrabold rounded-xl transition-all duration-300 ${direction === 'to_airport'
                                            ? "bg-white text-blue-600 shadow-md transform scale-[1.02]"
                                            : "text-gray-500 hover:text-gray-700 hover:bg-white/40"
                                            }`}
                                    >
                                        {t('form.to_airport') || "To Airport"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDirection('from_airport');
                                            setFromLocation('');
                                            setToLocation('');
                                        }}
                                        className={`px-8 py-2.5 text-sm font-extrabold rounded-xl transition-all duration-300 ${direction === 'from_airport'
                                            ? "bg-white text-blue-600 shadow-md transform scale-[1.02]"
                                            : "text-gray-500 hover:text-gray-700 hover:bg-white/40"
                                            }`}
                                    >
                                        {t('form.from_airport') || "From Airport"}
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-3">
                                {/* ... locations group ... */}
                                <div className="flex flex-col md:flex-row md:flex-[2.5] gap-4 md:gap-0 md:bg-gray-50/50 md:rounded-2xl md:border md:border-gray-200 md:shadow-sm">
                                    {/* Pickup */}
                                    <div className={`border border-gray-200 md:border-0 rounded-2xl md:rounded-none flex-1 group transition-all duration-300 relative ${direction === 'from_airport' ? 'z-30' : 'z-10'}`}>
                                        <div className="flex items-center px-4 py-4 relative bg-white md:bg-transparent h-full transition-all duration-300 hover:bg-gray-50/80 md:hover:bg-white/80 focus-within:bg-white focus-within:shadow-lg md:focus-within:shadow-none rounded-2xl md:rounded-none">
                                            <MapPin className="w-5 h-5 text-gray-400 mr-4 flex-shrink-0 transition-colors group-focus-within:text-blue-500" />
                                            <div className="flex-1 relative">
                                                {direction === 'from_airport' ? (
                                                    <>
                                                        <div className="h-[42px] flex items-center">
                                                            <CustomSelect
                                                                value={fromLocation}
                                                                onChange={(val) => setFromLocation(val)}
                                                                options={terminalOptions}
                                                                placeholder={t('form.select_terminal') || "Select Terminal"}
                                                                variant="flat"
                                                            />
                                                        </div>
                                                        {fromLocation === 'unknown_terminal' && (
                                                            <div className="absolute -bottom-6 left-0 text-[10px] text-blue-600 font-bold italic animate-pulse whitespace-nowrap">
                                                                ✓ {t('home.we_check_terminal')}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="h-[42px] flex items-center">
                                                        <GoogleSearch
                                                            onSelect={(place) => setFromLocation(place.formatted_address)}
                                                            placeholder={t('form.from') || "Pickup Location"}
                                                            className="w-full outline-none text-sm text-gray-700 placeholder:text-gray-400 font-semibold bg-transparent truncate border-none focus:ring-0 p-0"
                                                            defaultValue={fromLocation}
                                                            onInputChange={(value) => setFromLocation(value)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dropoff */}
                                    <div className={`border border-gray-200 md:border-0 md:border-l md:border-gray-200 rounded-2xl md:rounded-none flex-1 group transition-all duration-300 relative ${direction === 'to_airport' ? 'z-30' : 'z-10'}`}>
                                        <div className="flex items-center px-4 py-4 relative bg-white md:bg-transparent h-full transition-all duration-300 hover:bg-gray-50/80 md:hover:bg-white/80 focus-within:bg-white focus-within:shadow-lg md:focus-within:shadow-none rounded-2xl md:rounded-none">
                                            <MapPin className="w-5 h-5 text-blue-500/60 mr-4 flex-shrink-0 transition-colors group-focus-within:text-blue-600" />
                                            <div className="flex-1 relative">
                                                {direction === 'to_airport' ? (
                                                    <>
                                                        <div className="h-[42px] flex items-center">
                                                            <CustomSelect
                                                                value={toLocation}
                                                                onChange={(val) => setToLocation(val)}
                                                                options={terminalOptions}
                                                                placeholder={t('form.select_terminal') || "Select Terminal"}
                                                                variant="flat"
                                                            />
                                                        </div>
                                                        {toLocation === 'unknown_terminal' && (
                                                            <div className="absolute -bottom-6 left-0 text-[10px] text-blue-600 font-bold italic animate-pulse whitespace-nowrap">
                                                                ✓ {t('home.we_check_terminal')}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="h-[42px] flex items-center">
                                                        <GoogleSearch
                                                            onSelect={(place) => setToLocation(place.formatted_address)}
                                                            placeholder={t('form.to') || "Dropoff Location"}
                                                            className="w-full outline-none text-sm text-gray-700 placeholder:text-gray-400 font-semibold bg-transparent truncate border-none focus:ring-0 p-0"
                                                            defaultValue={toLocation}
                                                            onInputChange={(value) => setToLocation(value)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Date/Time/Pax/Suitcases Group */}
                                <div className="grid grid-cols-2 md:flex md:flex-row md:flex-1 gap-2 md:gap-0 md:bg-gray-50/50 md:rounded-2xl md:border md:border-gray-200 md:shadow-sm">
                                    {/* Date */}
                                    <label className="border border-gray-200 md:border-0 rounded-2xl md:rounded-none px-2.5 py-2.5 md:py-2 flex items-center gap-2 md:gap-3 bg-white md:bg-transparent hover:bg-gray-50 transition-all duration-300 cursor-pointer group focus-within:bg-white focus-within:shadow-lg md:focus-within:shadow-none z-10 flex-1">
                                        <Calendar className="w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                        <div className="flex flex-col w-full">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('form.departure_date') || "Date"}</span>
                                            <input
                                                type="date"
                                                min={minDate}
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                className="outline-none text-sm font-bold text-gray-700 bg-transparent w-full p-0 border-none focus:ring-0 cursor-pointer"
                                            />
                                        </div>
                                    </label>

                                    {/* Time */}
                                    <label className="border border-gray-200 md:border-0 md:border-l md:border-gray-200 rounded-2xl md:rounded-none px-2 py-2.5 md:px-4 md:py-2 flex items-center gap-1.5 md:gap-3 bg-white md:bg-transparent hover:bg-gray-50 transition-all duration-300 cursor-pointer group focus-within:bg-white focus-within:shadow-lg md:focus-within:shadow-none z-10 flex-1">
                                        <Clock className="w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                        <div className="flex flex-col w-full justify-center">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('form.time') || "Time"}</span>
                                            <div className="flex items-center gap-1">
                                                <select
                                                    value={time ? time.split(':')[0] : ''}
                                                    onChange={(e) => {
                                                        const newHour = e.target.value;
                                                        const parts = time ? time.split(':') : ['00', '00'];
                                                        const currentMinute = parts[1] || '00';
                                                        setTime(`${newHour}:${currentMinute}`);
                                                    }}
                                                    className="outline-none text-sm font-extrabold text-gray-700 bg-transparent min-w-0 flex-1 p-0 border-none focus:ring-0 cursor-pointer appearance-none text-center"
                                                >
                                                    <option value="" disabled>HH</option>
                                                    {hours.map((h) => (
                                                        <option key={h} value={h}>{h}</option>
                                                    ))}
                                                </select>
                                                <span className="text-blue-500/40 font-bold text-sm">:</span>
                                                <select
                                                    value={time ? time.split(':')[1] : ''}
                                                    onChange={(e) => {
                                                        const newMinute = e.target.value;
                                                        const parts = time ? time.split(':') : ['00', '00'];
                                                        const currentHour = parts[0] || '12';
                                                        setTime(`${currentHour}:${newMinute}`);
                                                    }}
                                                    className="outline-none text-sm font-extrabold text-gray-700 bg-transparent min-w-0 flex-1 p-0 border-none focus:ring-0 cursor-pointer appearance-none text-center"
                                                >
                                                    <option value="" disabled>MM</option>
                                                    {minutes.map((m) => (
                                                        <option key={m} value={m}>{m}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </label>

                                    {/* Passengers */}
                                    <div className="border border-gray-200 md:border-0 md:border-l md:border-gray-200 rounded-2xl md:rounded-none px-2.5 py-2.5 md:py-2 flex items-center gap-2 md:gap-3 bg-white md:bg-transparent hover:bg-gray-50 transition-all duration-300 group focus-within:bg-white focus-within:shadow-lg md:focus-within:shadow-none z-10 flex-1 animate-fade-in">
                                        <Users className="w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                        <div className="flex flex-col w-full">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('form.passengers') || "Pax"}</span>
                                            <select
                                                value={passengers}
                                                onChange={(e) => setPassengers(Number(e.target.value))}
                                                className="w-full outline-none text-sm font-extrabold text-gray-700 bg-transparent p-0 cursor-pointer border-none focus:ring-0"
                                            >
                                                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                                                    <option key={n} value={n}>{n}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Suitcases */}
                                    <div className="border border-gray-200 md:border-0 md:border-l md:border-gray-200 rounded-2xl md:rounded-none px-2.5 py-2.5 md:py-2 flex items-center gap-2 md:gap-3 bg-white md:bg-transparent hover:bg-gray-50 transition-all duration-300 group focus-within:bg-white focus-within:shadow-lg md:focus-within:shadow-none z-10 flex-1 animate-fade-in">
                                        <Briefcase className="w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                        <div className="flex flex-col w-full">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('form.suitcases') || "Maletas"}</span>
                                            <select
                                                value={suitcases}
                                                onChange={(e) => setSuitcases(Number(e.target.value))}
                                                className="w-full outline-none text-sm font-extrabold text-gray-700 bg-transparent p-0 cursor-pointer border-none focus:ring-0"
                                            >
                                                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                                                    <option key={n} value={n}>{n}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Book Ride Button */}
                            <div className="flex justify-end mt-2">
                                <button
                                    type="button"
                                    onClick={handleBookRide}
                                    disabled={!fromLocation || !toLocation || !date || !time}
                                    className="w-full md:w-auto md:px-10 py-5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-base font-black uppercase tracking-wider rounded-2xl transition-all duration-300 shadow-xl shadow-blue-600/30 disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap h-[64px] group"
                                >
                                    <span>{t('form.book_ride') || "Book Ride"}</span>
                                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                                        <ArrowLeft className="w-3 h-3 rotate-180" />
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* SEARCHING STEP (Simulated) */}
                    {step === 'searching' && (
                        <div className="flex flex-col items-center justify-center py-24 px-6 animate-in fade-in duration-500">
                            <div className="relative mb-12">
                                {/* Outer pulsing ring */}
                                <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping duration-[2000ms]"></div>
                                {/* Animated Spinner Container */}
                                <div className="relative w-32 h-32 bg-white rounded-full shadow-2xl flex items-center justify-center border-4 border-blue-500/10">
                                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                                    <Search className="w-12 h-12 text-blue-600 animate-pulse" />
                                </div>

                                {/* Orbiting dots */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-gray-100 rounded-full animate-spin duration-[8000ms]">
                                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-400 rounded-full shadow-lg shadow-blue-400/50"></div>
                                </div>
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 border border-gray-50 rounded-full animate-spin duration-[6000ms] [animation-direction:reverse]">
                                    <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-300 rounded-full shadow-lg"></div>
                                </div>
                            </div>

                            <div className="space-y-4 max-w-xs mx-auto text-center">
                                <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                                    {t('form.modal.searching_drivers') || "Searching for available drivers..."}
                                </h2>
                                <div className="flex justify-center gap-1.5">
                                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                </div>
                                <p className="text-gray-400 text-sm font-medium italic">
                                    {t('form.modal.connecting_vehicles') || "Connecting with vehicles near you..."}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* SUCCESS STEP */}
                    {step === 'success' && (
                        <div className="flex flex-col items-center text-center py-16 px-6 animate-in fade-in zoom-in-95 duration-500">
                            <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-10 shadow-inner ring-4 ring-green-50">
                                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                </svg>
                            </div>
                            <h2 className="text-4xl font-black mb-6 text-gray-900 tracking-tight">
                                {t('form.modal.success_title') || "¡Solicitud Enviada!"}
                            </h2>
                            <p className="text-gray-500 mb-12 text-xl leading-relaxed max-w-sm mx-auto">
                                {t('form.modal.success_message') || "Su solicitud ha sido recibida correctamente."}
                            </p>
                            <button
                                onClick={() => {
                                    setStep('search');
                                    setFromLocation("");
                                    setToLocation("");
                                }}
                                className="inline-flex items-center gap-3 bg-blue-600 text-white px-10 py-4 rounded-2xl font-black text-xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/25 active:scale-95 group"
                            >
                                <ArrowLeft className="w-6 h-6 transition-transform group-hover:-translate-x-1" />
                                <span>{t('navbar.home') || "Inicio"}</span>
                            </button>
                        </div>
                    )}

                    {/* DETAILS STEP */}
                    {step === 'details' && (
                        <div className="flex flex-col md:flex-row gap-6 animate-in slide-in-from-right-4 duration-300 md:p-6 max-w-6xl mx-auto w-full">
                            {/* Mobile Order Summary (Visible only on mobile / hidden on md) */}
                            <div className="md:hidden space-y-3 w-full">
                                {/* Order Summary Card mobile (Collapsible) */}
                                <div
                                    onClick={() => setIsMobileSummaryExpanded(!isMobileSummaryExpanded)}
                                    className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm cursor-pointer"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="font-extrabold text-gray-800 text-sm flex items-center gap-1.5 select-none">
                                                {t('form.order_summary') || "Resumen del viaje"}
                                                {isMobileSummaryExpanded ? (
                                                    <ChevronDown className="w-4 h-4 text-gray-400 rotate-180 transition-transform" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-gray-400 transition-transform" />
                                                )}
                                            </h3>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t('form.total_price') || "Total price"} ({t('form.taxes_included') || "Taxes & fees included"})</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-[#10B981] font-black text-2xl">
                                                €{(() => {
                                                    const priceArray = carType === "van" ? priceVan : priceNormal;
                                                    if (!priceArray) return "0";
                                                    const [euros, cents] = priceArray;
                                                    return cents > 0 ? `${euros},${String(cents).padStart(2, '0')}` : `${euros}`;
                                                })()}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Expanded details list inside mobile card */}
                                    {isMobileSummaryExpanded && (
                                        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 animate-in fade-in duration-200">
                                            {/* Date header */}
                                            <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase pb-2">
                                                <Calendar className="w-4 h-4 text-blue-500" />
                                                <span>
                                                    {(() => {
                                                        if (!date) return "";
                                                        const parts = date.split('-');
                                                        if (parts.length === 3) {
                                                            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                            return `${parts[2]} ${months[parseInt(parts[1], 10) - 1] || ""}, ${parts[0]}`;
                                                        }
                                                        return date;
                                                    })()} | {time}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setStep('search');
                                                    }}
                                                    className="ml-auto text-blue-600 font-extrabold hover:underline"
                                                >
                                                    {t('form.modal.back') || "Back"}
                                                </button>
                                            </div>

                                            {/* Path representation (vertical line) */}
                                            <div className="relative pl-6 space-y-4 text-sm">
                                                {/* Vertical Connective Line */}
                                                <div className="absolute left-[7px] top-2.5 bottom-2.5 w-[2px] bg-emerald-500" />

                                                {/* Pickup Point */}
                                                <div className="relative">
                                                    <div className="absolute -left-[23px] top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-emerald-500 flex items-center justify-center">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                    </div>
                                                    <p className="font-bold text-gray-800 text-[10px] uppercase tracking-wider">{t('form.from') || "Pickup"}</p>
                                                    <p className="text-gray-600 font-semibold text-xs line-clamp-2 mt-0.5">
                                                        {fromLocation === "unknown_terminal"
                                                            ? (t('form.terminal_unknown') || "I don't know my terminal")
                                                            : fromLocation}
                                                    </p>
                                                </div>

                                                {/* Dropoff Point */}
                                                <div className="relative">
                                                    <div className="absolute -left-[23px] top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-emerald-500 flex items-center justify-center">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                    </div>
                                                    <p className="font-bold text-gray-800 text-[10px] uppercase tracking-wider">{t('form.to') || "Dropoff"}</p>
                                                    <p className="text-gray-600 font-semibold text-xs line-clamp-2 mt-0.5">
                                                        {toLocation === "unknown_terminal"
                                                            ? (t('form.terminal_unknown') || "I don't know my terminal")
                                                            : toLocation}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Specs Row: Vehicle Symbol, Pax Count, Bag Count */}
                                            <div className="flex items-center gap-4 pt-3 border-t border-gray-100 text-xs font-bold text-gray-500">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="capitalize">{carType === "van" ? (t('rates.van_title') || "Minivan") : (t('rates.normal_title') || "Sedan")}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Users className="w-4 h-4 text-gray-400" />
                                                    <span>{passengers} {passengers === 1 ? t('form.passenger') || "Pax" : t('form.passengers') || "Pax"}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Briefcase className="w-4 h-4 text-gray-400" />
                                                    <span>{suitcases} {suitcases === 1 ? t('form.suitcase') || "Maleta" : t('form.suitcases') || "Maletas"}</span>
                                                </div>
                                            </div>

                                            {/* Total Price Section inside mobile collapse */}
                                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                <div>
                                                    <p className="text-xs font-bold text-gray-800">{t('form.total_price') || "Total price"}</p>
                                                    <p className="text-[9px] text-gray-400 font-semibold">{t('form.taxes_included') || "Taxes & fees included"}</p>
                                                </div>
                                                <span className="text-[#10B981] font-black text-xl">
                                                    €{(() => {
                                                        const priceArray = carType === "van" ? priceVan : priceNormal;
                                                        if (!priceArray) return "0";
                                                        const [euros, cents] = priceArray;
                                                        return cents > 0 ? `${euros},${String(cents).padStart(2, '0')}` : `${euros}`;
                                                    })()}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Padlock Price Security Banner */}
                                <div className="bg-emerald-50 text-emerald-800 px-4 py-2.5 rounded-xl border border-emerald-100 text-xs font-semibold flex items-center gap-2">
                                    <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    <span>{t('form.secure_price') || "Secure this price now; prices may change soon."}</span>
                                </div>
                            </div>

                            {/* Left Column: Form */}
                            <form onSubmit={handleFinalBooking} className="flex-1 space-y-6">
                                <h2 className="text-2xl font-black text-gray-900 tracking-tight hidden md:block mb-4">
                                    {t('form.modal.details_title') || "Complete Booking"}
                                </h2>
                                <div className="space-y-4 md:grid md:grid-cols-2 md:gap-5 md:space-y-0">
                                    <div className="relative group">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder={t('form.name_placeholder') || "Full Name"}
                                            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm font-semibold text-gray-700 shadow-sm"
                                            required
                                        />
                                    </div>
                                    <div className="relative group">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder={t('form.email_placeholder') || "Email Address"}
                                            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm font-semibold text-gray-700 shadow-sm"
                                            required
                                        />
                                    </div>
                                    <div className="flex gap-3 md:col-span-2">
                                        <div className="w-[125px] relative flex-shrink-0">
                                            <select
                                                value={countryCode}
                                                onChange={(e) => setCountryCode(e.target.value)}
                                                className="w-full pl-4 pr-7 py-4 rounded-2xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none appearance-none text-sm font-bold text-gray-700 shadow-sm cursor-pointer"
                                            >
                                                <option value="+34">🇪🇸 +34</option>
                                                <option value="+44">🇬🇧 +44</option>
                                                <option value="+1">🇺🇸 +1</option>
                                                <option value="+33">🇫🇷 +33</option>
                                                <option value="+49">🇩🇪 +49</option>
                                                <option value="+39">🇮🇹 +39</option>
                                                <option value="+351">🇵🇹 +351</option>
                                                <option value="+31">🇳🇱 +31</option>
                                                <option value="+32">🇧🇪 +32</option>
                                                <option value="+41">🇨🇭 +41</option>
                                                <option value="+45">🇩🇰 +45</option>
                                                <option value="+7">🇷🇺 +7</option>
                                                <option value="+86">🇨🇳 +86</option>
                                                <option value="+81">🇯🇵 +81</option>
                                                <option value="+82">🇰🇷 +82</option>
                                                <option value="+91">🇮🇳 +91</option>
                                                <option value="+55">🇧🇷 +55</option>
                                                <option value="+52">🇲🇽 +52</option>
                                                <option value="+61">🇦🇺 +61</option>
                                                <option value="+971">🇦🇪 +971</option>
                                            </select>
                                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                        </div>
                                        <div className="flex-1 relative group">
                                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                            <input
                                                type="tel"
                                                value={number}
                                                onChange={handleNumberChange}
                                                placeholder={t('form.phone_placeholder') || "Phone Number"}
                                                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm font-semibold text-gray-700 shadow-sm"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Additional Info */}
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">{t('form.additional_notes') || "Additional Info (Optional)"}</label>
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={t('form.notes_placeholder') || "Flight number, special luggage, child seat..."}
                                            rows={4}
                                            className="w-full px-5 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 resize-none text-sm font-semibold text-gray-700 shadow-sm transition-all"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isCalculating || !name || !email || !number || number.length < 9}
                                    className="w-full py-5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-base font-black uppercase tracking-wider rounded-2xl transition-all duration-300 shadow-xl shadow-blue-600/30 disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-3 transform hover:scale-[1.01] active:scale-[0.99]"
                                >
                                    {isCalculating ? (
                                        <span className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <span>{t('form.confirm_booking') || "Confirm Booking"}</span>
                                            <ArrowLeft className="w-4 h-4 rotate-180" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Right Column: Desktop Order Summary (Visible only on md and up) */}
                            <div className="hidden md:block w-full md:w-[360px] flex-shrink-0">
                                <div className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-xl sticky top-4">
                                    {/* Barcelona Image Header */}
                                    <div className="relative h-[155px] w-full">
                                        <img
                                            src={parkGuellImg}
                                            alt="Barcelona Summary"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
                                        <div className="absolute bottom-4 left-4 text-white">
                                            <h3 className="text-lg font-black tracking-tight">{t('form.order_summary') || "Resumen del viaje"}</h3>
                                            <p className="text-xs text-white/80 font-bold uppercase tracking-wider">Barcelona</p>
                                        </div>
                                    </div>

                                    {/* Trip Detail Timeline */}
                                    <div className="p-5 space-y-5">
                                        {/* Date header */}
                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase pb-3 border-b border-gray-100">
                                            <Calendar className="w-4 h-4 text-blue-500" />
                                            <span>
                                                {(() => {
                                                    if (!date) return "";
                                                    const parts = date.split('-');
                                                    if (parts.length === 3) {
                                                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                        return `${parts[2]} ${months[parseInt(parts[1], 10) - 1] || ""}, ${parts[0]}`;
                                                    }
                                                    return date;
                                                })()} | {time}
                                            </span>
                                        </div>

                                        {/* Path representation (vertical line) */}
                                        <div className="relative pl-6 space-y-6 text-sm">
                                            {/* Vertical Connective Line */}
                                            <div className="absolute left-[7px] top-2.5 bottom-2.5 w-[2px] bg-emerald-500" />

                                            {/* Pickup Point */}
                                            <div className="relative">
                                                <div className="absolute -left-[23px] top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-emerald-500 flex items-center justify-center">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                </div>
                                                <p className="font-bold text-gray-800 text-[10px] uppercase tracking-wider">{t('form.from') || "Pickup"}</p>
                                                <p className="text-gray-600 font-semibold text-xs line-clamp-2 mt-0.5">
                                                    {fromLocation === "unknown_terminal"
                                                        ? (t('form.terminal_unknown') || "I don't know my terminal")
                                                        : fromLocation}
                                                </p>
                                            </div>

                                            {/* Dropoff Point */}
                                            <div className="relative">
                                                <div className="absolute -left-[23px] top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-emerald-500 flex items-center justify-center">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                </div>
                                                <p className="font-bold text-gray-800 text-[10px] uppercase tracking-wider">{t('form.to') || "Dropoff"}</p>
                                                <p className="text-gray-600 font-semibold text-xs line-clamp-2 mt-0.5">
                                                    {toLocation === "unknown_terminal"
                                                        ? (t('form.terminal_unknown') || "I don't know my terminal")
                                                        : toLocation}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Specs Row: Vehicle Symbol, Pax Count, Bag Count */}
                                        <div className="flex items-center gap-4 py-3 border-t border-b border-gray-100 text-xs font-bold text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <span className="capitalize">{carType === "van" ? (t('rates.van_title') || "Minivan") : (t('rates.normal_title') || "Sedan")}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Users className="w-4 h-4 text-gray-400" />
                                                <span>{passengers} {passengers === 1 ? t('form.passenger') || "Pax" : t('form.passengers') || "Pax"}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Briefcase className="w-4 h-4 text-gray-400" />
                                                <span>{suitcases} {suitcases === 1 ? t('form.suitcase') || "Maleta" : t('form.suitcases') || "Maletas"}</span>
                                            </div>
                                        </div>

                                        {/* Price section */}
                                        <div className="flex items-center justify-between pt-2">
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{t('form.total_price') || "Total price"}</p>
                                                <p className="text-[10px] text-gray-400 font-semibold">{t('form.taxes_included') || "Taxes & fees included"}</p>
                                            </div>
                                            <span className="text-[#10B981] font-black text-3xl">
                                                €{(() => {
                                                    const priceArray = carType === "van" ? priceVan : priceNormal;
                                                    if (!priceArray) return "0";
                                                    const [euros, cents] = priceArray;
                                                    return cents > 0 ? `${euros},${String(cents).padStart(2, '0')}` : `${euros}`;
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Error Modal */}
            {
                showErrorModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center animate-in zoom-in-95 duration-200">
                            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('form.modal.error_title') || "Booking Failed"}</h3>
                            <p className="text-gray-600 mb-6">
                                {typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage}
                            </p>
                            <button
                                onClick={() => setShowErrorModal(false)}
                                className="w-full py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                {t('form.modal.close') || "Close"}
                            </button>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

export default MainForm;